import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef, NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {FeatureFlagsService} from "../../core/services/feature-flag/feature-flags.service";

import {LegacyNavigateService} from "../../core/services/legacy-navigate/legacy-navigate.service";
import {RequestService} from "../../core/services/request/request.service";
import {CmdbDataService} from "../../core/services/cmdb-data/cmdb-data.service";

import * as go from 'src/libs/gojs/release/go';
import {GraphLinksModel} from "src/libs/gojs/release/go";
import {ArrangingLayout} from "../../gojs/extensions/ArrangingLayout";
import {DiagramComponent} from "../../gojs/components/diagram/diagram.component";

import { ResizeObserver } from '@juggle/resize-observer';
import {UtilService} from "../../core/services/util/util.service";
import {Subscription} from "rxjs";
import {ActivatedRoute, Router} from "@angular/router";
import {CmdbNodeRecordModel} from "../../core/services/cmdb-data/cmdb-node-record.model";
import {CmdbRelationshipRecordModel} from "../../core/services/cmdb-data/cmdb-relationship-record.model";
import {CmdbPeerRelationshipRecordModel} from "../../core/services/cmdb-data/cmdb-peer-relationship-record.model";
import {CmdbRelationshipTypeRecordModel} from "../../core/services/cmdb-data/cmdb-relationship-type-record.model";
import {CmdbPeerRelationshipTypeRecordModel} from "../../core/services/cmdb-data/cmdb-peer-relationship-type-record.model";
import {CmdbMetadataRecordModel} from "../../core/services/cmdb-data/cmdb-metadata-record.model";
import {CmdbNodeDataModel} from "./cmdb-node-data.model";
import {CmdbRelationshipDataModel} from "./cmdb-relationship-data.model";
import {CmdbPeerRelationshipDataModel} from "./cmdb-peer-relationship-data.model";
import {CmdbLogicalClassificationRecordModel} from "../../core/services/cmdb-data/cmdb-logical-classification-record.model";
import {CmdbTableHierarchyModel} from "../../core/services/cmdb-data/cmdb-table-hierarchy.model";
import {InternationalizationService} from "../../core/services/internationalization/internationalization.service";

// Shortcut for GoJS make method, in the examples this is $, but that conflicts with jQuery
const $$ = go.GraphObject.make;

enum DisplayType {
  CMDB = 1,
  SpecificCI,
  LogicalTree,
  PhysicalTree,
}

// Anything else is considered a specific CI
const RouteParamToDisplayType: { [key: string]: DisplayType; }  = {
  'Default': DisplayType.CMDB,
  'LogicalTree': DisplayType.LogicalTree,
  'PhysicalTree': DisplayType.PhysicalTree
}

@Component({
  selector: 'sb-cmdb-page',
  templateUrl: './cmdb-page.component.html',
  styleUrls: ['./cmdb-page.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CmdbPageComponent implements OnInit, AfterViewInit, OnDestroy {

  // Data

  private nodes: CmdbNodeRecordModel[] = [];
  private relationships: CmdbRelationshipRecordModel[] = [];
  private peerRelationships: CmdbPeerRelationshipRecordModel[] = [];
  private relationshipTypes: CmdbRelationshipTypeRecordModel[] = [];
  private peerRelationshipTypes: CmdbPeerRelationshipTypeRecordModel[] = [];
  private metadata: CmdbMetadataRecordModel[] = [];
  private logicalClassifications: CmdbLogicalClassificationRecordModel[] = [];
  private tableHierarchy: CmdbTableHierarchyModel = {};
  private cmdbCharacterFontSizes = {};

  private idToNodeMap: { [key: string]: CmdbNodeRecordModel } = {};
  private idToRelationshipMap: { [key: string]: CmdbRelationshipRecordModel } = {};
  private idToPeerRelationshipMap: { [key: string]: CmdbPeerRelationshipRecordModel } = {};
  private idToRelationshipTypesMap: { [key: string]: CmdbRelationshipTypeRecordModel } = {};
  private idToPeerRelationshipTypesMap: { [key: string]: CmdbPeerRelationshipTypeRecordModel } = {};

  // Internal state

  private lastInfoboxId: string = null;
  private displayType: DisplayType = DisplayType.CMDB;
  private activatedRouteSubscription: Subscription | null;
  private individualCI: string;
  private componentFirstDisplay = true;

  // GoJS

  private gojsDiagram: go.Diagram;

  @ViewChild('gojsDiagram', { static: false }) public gojsDiagramComponent: DiagramComponent;

  private gojsControls: go.Diagram;

  @ViewChild('gojsControls', { static: false }) public gojsControlsComponent: DiagramComponent;

  // Exposed diagram data

  public diagramNodeData: go.ObjectData[] = [];
  public diagramLinkData: go.ObjectData[] = [];
  public diagramModelData: go.ObjectData = { prop: 'value' };

  public skipsDiagramUpdate = false;

  // Exposed state

  public title: string = "";

  // Exposed overview data

  public observedDiagram = null;

  // Resize support

  @ViewChild("diagramColumn", {static: false})
  private diagramColumn: ElementRef;

  private resizeObserver: ResizeObserver;

  // Subscriptions

  private cmdbSubscription: Subscription;

  // Constructor

  constructor(private el: ElementRef,
              private router: Router,
              private activatedRoute: ActivatedRoute,
              private cdr: ChangeDetectorRef,
              private featureFlagsService: FeatureFlagsService,
              private requestService: RequestService,
              private cmdbDataService: CmdbDataService,
              private legacyNavigateService: LegacyNavigateService,
              private i18nService: InternationalizationService,
              private utilService: UtilService,
              private zone: NgZone) {

    this.cmdbSubscription = this.cmdbDataService.cmdbContentLoaded.subscribe((_) => {
      this.initData();
    });

  }

  ngOnInit(): void {
    // Wipe out the page context
    (window as any).KO_PAGE_CONTEXT.clear();

    // Wipe out the Knockout content.
    const pageContentMain = document.getElementById("page-content-main");
    if (pageContentMain) {
      pageContentMain.innerHTML = "";
    }
    const errorConsole = document.getElementById("error-console");
    if (errorConsole) {
      errorConsole.innerHTML = "";
    }

    // Mark as portal page
    document.getElementById("detailMain")?.classList?.add("sb-cmdb-page-content");

    // Resize support
    this.resizeObserver = new ResizeObserver((entries, observer) => {
      this.zone.run(() => {
        entries.forEach((e) => {
          if (e.target === this.diagramColumn.nativeElement) {
            if (this.gojsDiagram) {
              const requiredWidth = e.contentRect.width + "px";
              this.gojsDiagram.div.style.width = requiredWidth;
              this.gojsDiagram.requestUpdate();
            }
          } else if (e.target === document.body) {
            if (this.gojsDiagram && this.diagramColumn) {
              const requiredHeight = (e.contentRect.height - this.diagramColumn.nativeElement.getBoundingClientRect().top - 80) + "px";
              this.diagramColumn.nativeElement.style.height = requiredHeight;
              this.gojsDiagram.div.style.height = requiredHeight;
              this.gojsDiagram.requestUpdate();
            }
          }
        });
      });
    });
    this.resizeObserver.observe(document.body);

    // Identify the displayType from the route.
    this.activatedRouteSubscription = this.activatedRoute.paramMap.subscribe(async params => {
      let id = params.get("id"); // 'id' might be a real id or a key indicating some other kind of thing to display
      this.displayType = RouteParamToDisplayType[id] || DisplayType.SpecificCI;
      if (this.displayType === DisplayType.SpecificCI) {
        this.individualCI = id;
      }
      // We may be changing routes on the same CMDB component so we need to clear it all first.
      if (!this.componentFirstDisplay) {
        this.clearDiagram();
        this.displayDiagram();
      }
    });

  }

  async ngAfterViewInit() {
    this.resizeObserver.observe(this.diagramColumn.nativeElement);

    await this.displayDiagram();

    this.componentFirstDisplay = false;
  }

  ngOnDestroy(): void {
    this.cmdbSubscription && this.cmdbSubscription.unsubscribe();
    this.resizeObserver.unobserve(this.diagramColumn.nativeElement);
    this.resizeObserver.unobserve(document.body);
    this.activatedRouteSubscription.unsubscribe();
  }

  // Methods used in the template

  public getInitDiagram(): () => go.Diagram {
    return () => { return this.initDiagram() };
  }

  public getInitOverview(): () => go.Overview {
    return () => { return this.initOverview() };
  }

  public getInitControls(): () => go.Diagram {
    return () => { return this.initControls() };
  }

  // Common methods

  private async displayDiagram() {
    if (this.displayType === DisplayType.CMDB) {
      await this.initCmdb();
    } else if (this.displayType === DisplayType.LogicalTree) {
      await this.initLogicalTree();
    } else if (this.displayType === DisplayType.PhysicalTree) {
      await this.initPhysicalTree();
    } else {
      await this.initIndividualCI();
    }
  }

  private clearDiagram() {
    this.removeAllNodes();
    this.removeAllLinks();
  }

  // Tree methods

  private async initLogicalTree() {
    await this.initData();
    this.linkOverviewToDiagram();
    this.initControlButtons();
    this.initDecorationsLogicalTree();
    this.utilService.tickThen(() => {this.populateLogicalTreeDiagram()});
  }

  private async initPhysicalTree() {
    await this.initData();
    this.linkOverviewToDiagram();
    this.initControlButtons();
    this.initDecorationsPhysicalTree();
    this.utilService.tickThen(() => {this.populatePhysicalTreeDiagram()});
  }

  // Individual CI methods

  private async initIndividualCI() {
    await this.initData();
    this.linkOverviewToDiagram();
    this.initControlButtons();
    this.initInfoBox();
    this.initDecorationsIndividualCI();
    this.utilService.tickThen(() => {this.populateIndividualDiagram()});
  }

  // CMDB methods

  private async initCmdb() {
    await this.initData();
    this.linkOverviewToDiagram();
    this.initControlButtons();
    this.initInfoBox();
    this.initDecorationsCmdb();
    this.utilService.tickThen(() => {this.populateDiagram()});
  }

  private initDecorationsCmdb() {
    this.title = "Full CMDB";
  }

  private initDecorationsIndividualCI() {
    let name = "Unknown";
    if (this.individualCI) {
      const node = this.idToNodeMap[this.individualCI];
      if (node) {
        name = node.Name.displayValue;
      }
    }
    this.title = `Configuration items related to <em>${name}</em>`;
  }

  private initDecorationsPhysicalTree() {
    this.title = "CMDB physical tree";
  }

  private initDecorationsLogicalTree() {
    this.title = "CMDB logical tree";
  }

  private async initData() {
    this.idToNodeMap = {};
    this.idToRelationshipMap = {};
    this.idToPeerRelationshipMap = {};
    this.idToRelationshipTypesMap = {};
    this.idToPeerRelationshipTypesMap = {};

    if (this.displayType == DisplayType.SpecificCI) {
      await this.ensureNodeLoaded(this.individualCI, true, true, true);
    } else if (this.displayType == DisplayType.CMDB) {
      this.nodes = await this.cmdbDataService.getNodes();
      this.relationships = await this.cmdbDataService.getRelationships();
      this.peerRelationships = await this.cmdbDataService.getPeerRelationships();
    } else {
      this.nodes = [];
      this.relationships = [];
      this.peerRelationships = []
    }

    this.relationshipTypes = this.cmdbDataService.getRelationshipTypes();
    this.peerRelationshipTypes = this.cmdbDataService.getPeerRelationshipTypes();
    this.metadata = this.cmdbDataService.getMetadata();
    this.logicalClassifications = this.cmdbDataService.getLogicalClassifications();
    this.tableHierarchy = this.cmdbDataService.getTableHierarchy();
    this.cmdbCharacterFontSizes = this.cmdbDataService.getCmdbCharacterFontSizes();

    this.nodes.forEach((node) => {
      this.idToNodeMap[node.id] = node;
    });

    this.relationships.forEach((rel) => {
      this.idToRelationshipMap[rel.id] = rel;
    });

    this.peerRelationships.forEach((rel) => {
      this.idToPeerRelationshipMap[rel.id] = rel;
    });

    this.relationshipTypes.forEach((rel) => {
      this.idToRelationshipTypesMap[rel.id] = rel;
    });

    this.peerRelationshipTypes.forEach((rel) => {
      this.idToPeerRelationshipTypesMap[rel.id] = rel;
    });
  }

  private initDiagram(): go.Diagram {
    const dia = $$(go.Diagram);

    // Needs a model to start with or the Angular component complains
    dia.model = $$(go.GraphLinksModel,
      {
        linkToPortIdProperty: 'toPort',
        linkFromPortIdProperty: 'fromPort',
        linkKeyProperty: 'key'
      }
    );

    this.gojsDiagram = dia;

    return dia;
  }

  private initControls(): go.Diagram {
    const dia = $$(go.Diagram, {
      isReadOnly: true,
      allowResize: false,
      allowZoom: false,
      allowHorizontalScroll: false,
      allowVerticalScroll: false,
      "draggingTool.isEnabled": false,
      "panningTool.isEnabled": false,
      "dragSelectingTool.isEnabled": false,
      'undoManager.isEnabled': false,
      "toolManager.hoverDelay": 1000,  // how quickly tooltips are shown
    });

    this.gojsControls = dia;

    return dia;
  }

  private initControlButtons() {
    this.gojsControls.add(
      $$(go.Node, "Auto",
        { selectable: false, movable: false},
        $$(go.Panel, "Table",
          $$(go.RowColumnDefinition, { width: 25 }),
          $$(go.RowColumnDefinition, { isRow: true, height: 25 }),
          $$("Button",
            {
              row: 0,
              column: 0,
              margin: 0,
              toolTip: $$("ToolTip", $$(go.TextBlock, { margin: 4, text: "Zoom in" })),
              click: () => { this.zoomIn() },
              "ButtonBorder.stroke": null,
              "ButtonBorder.fill": null,
            },
            $$(go.TextBlock, { font: '16pt Material Symbols Outlined', stroke: '#425175', text: 'zoom_in' } )),
          $$("Button",
            {
              row: 0,
              column: 3,
              margin: 0,
              toolTip: $$("ToolTip", $$(go.TextBlock, { margin: 4, text: "Zoom out" })),
              click: () => { this.zoomOut() },
              "ButtonBorder.stroke": null,
              "ButtonBorder.fill": null,
            },
            $$(go.TextBlock, { font: '16pt Material Symbols Outlined', stroke: '#425175', text: 'zoom_out' } )),
          $$("Button",
            {
              row: 1,
              column: 1,
              margin: 0,
              toolTip: $$("ToolTip", $$(go.TextBlock, { margin: 4, text: "Reset" })),
              click: () => { this.resetDiagram() },
              "ButtonBorder.stroke": null,
              "ButtonBorder.fill": null,
            },
            $$(go.TextBlock, { font: '16pt Material Symbols Outlined', stroke: '#425175', text: 'refresh' })),
          $$("Button",
            {
              row: 2,
              column: 0,
              margin: 0,
              toolTip: $$("ToolTip", $$(go.TextBlock, { margin: 4, text: "Reset zoom" })),
              click: () => { this.resetZoom() },
              "ButtonBorder.stroke": null,
              "ButtonBorder.fill": null,
            },
            $$(go.TextBlock, { font: '16pt Material Symbols Outlined', stroke: '#425175', text: 'target' })),
          $$("Button",
            {
              row: 2,
              column: 3,
              margin: 0,
              toolTip: $$("ToolTip", $$(go.TextBlock, { margin: 4, text: "Zoom to fit" })),
              click: () => { this.zoomToFit() },
              "ButtonBorder.stroke": null,
              "ButtonBorder.fill": null,
            },
            $$(go.TextBlock, { font: '16pt Material Symbols Outlined', stroke: '#425175', text: 'zoom_in_map' })),
        )
      )
    );
  }

  private initInfoBox() {
    // Make sure the infoBox is hidden if the user tries to mouse over it
    document.getElementById("cmdb-info-box-holder").addEventListener("mousemove", function() {
      const box = document.getElementById("cmdb-info-box");
      box.style.left = parseInt(box.style.left) + "px";
      box.style.top = parseInt(box.style.top) + 30 + "px";
    }, false);

    // Make sure the infoBox is hidden when the mouse is not over the diagram
    document.getElementsByClassName("cmdb-diagram")[0].addEventListener("mouseout", (e) => {

      const infoBox = document.getElementById("cmdb-info-box");
      const elem = document.elementFromPoint((e as any).clientX, (e as any).clientY);
      if (elem !== null && (elem === infoBox || elem.parentNode === infoBox)) {
        const box = document.getElementById("cmdb-info-box-holder");
        box.style.left = parseInt(box.style.left) + "px";
        box.style.top = parseInt(box.style.top) + 30 + "px";
      } else {
        const box = document.getElementById("cmdb-info-box-holder");
        box.innerHTML = "";
      }
    }, false);
  }

  private defineNodeTemplates() {

    const simpleTemplate =
      $$(go.Node, "Spot",
        {
          contextMenu: this.initContextMenu(),
          toolTip: this.initToolTip(),
          selectionAdorned: false,
          // This will stop right click for context menu, but some people still want to do that to show the menu.
          // contextClick: function(e: go.InputEvent, node: go.GraphObject) {
          //   e.handled = true;
          // }
        },
        $$(go.Panel, 'Vertical',
          $$(go.Panel, "Spot",
            {
              portId: ""
            },

            $$(go.Panel, "Spot",
              $$(go.TextBlock,
                { width: 50, visible: false, stroke: '#425175', textAlign: "center"},
                new go.Binding("text", "icon"),
                new go.Binding("font", "iconFont"),
                new go.Binding("visible", "icon", (t) => { return !!t; })),
            ),

            $$("Button",
              {
                // shadowVisible: false,
                alignment: go.Spot.TopLeft,
                click: (e: go.InputEvent, obj: go.GraphObject) => {
                  e.diagram.commandHandler.showContextMenu(obj);
                  // If the context menu is invoked we don't want to see the tooltip
                  e.diagram.toolManager.hideToolTip();
                },
                "ButtonBorder.fill": 'transparent',
                "ButtonBorder.stroke": '#425175',
                "_buttonStrokeOver": '#425175'
              },
              $$(go.TextBlock,
                {
                  font: '10px Material Symbols Outlined',
                  stroke: '#425175',
                  textAlign: "center",
                  background: 'transparent',
                  text: 'density_small'
                }),
              ),

          ),

          $$(go.TextBlock, { font: "bold 10pt serif", shadowVisible: false, margin: 4, textAlign: "center" },
            new go.Binding('text', 'label')),
        ),
      );

    const nodeTemplateMap = new go.Map<string, go.Part>([
      { key: "", value: simpleTemplate},
    ]);

    this.gojsDiagram.nodeTemplateMap = nodeTemplateMap;
  }

  private defineLinkTemplates() {
    const directedTemplate =
      $$(go.Link,
        {
          routing: go.Link.AvoidsNodes,
          corner: 10,
        },
        $$(go.Shape, { isPanelMain: true, stroke: "transparent", strokeWidth: 8 }),  // thick undrawn path
        $$(go.Shape, { isPanelMain: true, stroke: "#FC7307", strokeWidth: 2 }),          // this is the link shape (the line)
        $$(go.Shape, { toArrow: "Standard", stroke: "#FC7307", fill: "#FC7307" }),                                       // this is an arrowhead
        $$(go.TextBlock, { font: "italic 8pt serif" }, new go.Binding("text", "label"),
          { segmentOffset: new go.Point(0, -10),
            segmentOrientation: go.Link.OrientUpright }),
        {
          // a mouse-over highlights the link by changing the first main path shape's stroke:
          mouseEnter: function(e, link) { (link as any).elt(0).stroke = "rgba(0,90,156,0.3)"; },
          mouseLeave: function(e, link) { (link as any).elt(0).stroke = "transparent"; }
        }
      );

    const peerTemplate =
      $$(go.Link,
        {
          routing: go.Link.AvoidsNodes,
          corner: 10,
        },
        $$(go.Shape, { isPanelMain: true, stroke: "transparent", strokeWidth: 8 }),  // thick undrawn path
        $$(go.Shape, { isPanelMain: true, stroke: "#D61A46", strokeWidth: 2 }),          // this is the link shape (the line)
        $$(go.TextBlock, { font: "italic 8pt serif" }, new go.Binding("text", "label"),
          { segmentOffset: new go.Point(0, -10),
            segmentOrientation: go.Link.OrientUpright }),
        {
          // a mouse-over highlights the link by changing the first main path shape's stroke:
          mouseEnter: function(e, link) { (link as any).elt(0).stroke = "rgba(0,90,156,0.3)"; },
          mouseLeave: function(e, link) { (link as any).elt(0).stroke = "transparent"; }
        }
      );

    const linkTemplateMap = new go.Map<string, go.Link>([
      { key: "", value: directedTemplate},
      { key: "CMDBPeerRelationship", value: peerTemplate}
    ]);

    this.gojsDiagram.linkTemplateMap = linkTemplateMap;
  }

  private initContextMenu() {

    const buildButton = (label: string, callback: (data: CmdbNodeDataModel) => void) => {
      return $$("ContextMenuButton",
        $$(go.TextBlock, label, { alignment: go.Spot.Left, margin: 2, stroke: "#425175", font: "normal normal 13px 'Source Sans Pro', 'sans-serif'"}),
        {
          click: (e: go.InputEvent, obj: go.GraphObject) => {
            callback(obj?.part?.data);
          }
        },
      );
    };

    return $$("ContextMenu",
      buildButton("Inspect CI", (data) => this.inspectNode(data)),
      buildButton("Expand providers", (data) => this.expandProviders(data)),
      // buildButton("Expand all providers", (data) => this.expandAllProviders(data)),
      buildButton("Expand dependents", (data) => this.expandDependents(data)),
      // buildButton("Expand all dependents", (data) => this.expandAllDependents(data)),
      buildButton("Expand peers", (data) => this.expandPeers(data)),
      buildButton("Expand all", (data) => this.expandAll(data)),
      buildButton("Focus on CI", (data) => this.focusCI(data)),
      buildButton("Remove CI", (data) => this.removeCI(data)),
    );
  }

  private initOverview(): go.Overview {
    return $$(go.Overview, {
      box: $$(go.Part, {
          selectable: true,
          selectionObjectName: 'BOXSHAPE',
          locationObjectName: 'BOXSHAPE',
          resizeObjectName: 'BOXSHAPE',
          cursor: 'move',
          selectionAdorned: false,
      },
        $$(go.Shape, {
            stroke: 'darkorange',
            strokeWidth: 2,
            strokeDashArray: [4, 8],
            fill: 'transparent',
            name: 'BOXSHAPE',
          }),
      ),
    });
  }

  private linkOverviewToDiagram() {
    if (this.observedDiagram) {
      return;
    }
    this.observedDiagram = this.gojsDiagramComponent.diagram;
    this.cdr.detectChanges();
  }

  private prepareDiagram() {
    const dia = this.gojsDiagram;

    dia.toolManager.hoverDelay = 500;

    dia.toolManager.draggingTool.isEnabled = true;
    dia.toolManager.dragSelectingTool.isEnabled = true;

    dia.model = $$(go.GraphLinksModel,
      {
        linkToPortIdProperty: 'toPort',
        linkFromPortIdProperty: 'fromPort',
        linkKeyProperty: 'key'
      }
    );

    dia.layout = $$(ArrangingLayout,
      {
        primaryLayout: $$(go.LayeredDigraphLayout, {
          layerSpacing: 50,
          columnSpacing: 50,
          direction: 0,

        }),
        // arrangingLayout: $$(go.CircularLayout, { nodeDiameterFormula: go.CircularLayout.Circular, spacing: 30 }),
        arrangingLayout: $$(go.GridLayout, { wrappingColumn: 3, alignment: go.GridLayout.Position }),
        sideLayout: $$(go.GridLayout),
        prepareSideLayout: function(lay, coll, b) {
          // called once for the sideLayout - adjust how wide the GridLayout lays out
          lay.wrappingWidth = Math.max(b.width, this.diagram.viewportBounds.width);
        }
      });

    this.defineNodeTemplates();

    this.defineLinkTemplates();
  }

  private populateDiagram() {
    this.prepareDiagram();

    const model = (this.gojsDiagram.model as GraphLinksModel);

    const newNodes = this.generateMultipleNodeData(this.nodes);

    newNodes.forEach((n) => {
      model.addNodeData(n);
    });

    const newLinks = this.generateMultipleLinkData(this.relationships);

    const newPeerLinks = this.generateMultiplePeerLinkData(this.peerRelationships);

    newLinks.forEach((n) => {
      model.addLinkData(n);
    });

    newPeerLinks.forEach((n) => {
      model.addLinkData(n);
    });

    this.cdr.detectChanges();
  }

  private populateIndividualDiagram() {
    this.prepareDiagram();

    const model = (this.gojsDiagram.model as GraphLinksModel);
    const node = this.idToNodeMap[this.individualCI];
    const newNode = this.generateNodeData(node);
    model.addNodeData(newNode);
    this.expandPeers(node.id);
    this.expandProviders(node.id);
    this.expandDependents(node.id);

    this.cdr.detectChanges();
  }

  private async ensureNodeLoaded(id: string,
                                 includeProvider: boolean,
                                 includeDependent: boolean,
                                 includePeer: boolean) {
    if (!this.idToNodeMap[id]) {
      const nodes = await this.cmdbDataService.getNode(id);
      nodes.forEach(node => {
        this.nodes.push(node);
        this.idToNodeMap[node.id] = node;
      });
    }

    if (includeProvider || includeDependent) {
      const relationships = await this.cmdbDataService.getRelationshipsFor(id);
      relationships.forEach(rel => {
        if (!this.idToRelationshipMap[rel.id]) {
          this.relationships.push(rel);
          this.idToRelationshipMap[rel.id] = rel;
        }
      });

      for (const rel of relationships) {
        if (includeDependent) {
          if (rel.Provider.value && !this.idToNodeMap[rel.Provider.value as string]) {
            await this.ensureNodeLoaded(rel.Provider.value as string, false, false, false);
          }
        }
        if (includeProvider) {
          if (rel.Dependent.value && !this.idToNodeMap[rel.Dependent.value as string]) {
            await this.ensureNodeLoaded(rel.Dependent.value as string, false, false, false);
          }
        }
      }
    }

    if (includePeer) {
      const peerRelationships = await this.cmdbDataService.getPeerRelationshipsFor(id);
      peerRelationships.forEach(rel => {
        if (!this.idToPeerRelationshipMap[rel.id]) {
          this.peerRelationships.push(rel);
          this.idToPeerRelationshipMap[rel.id] = rel;
        }
      });

      for (const rel of peerRelationships) {
        for (const peer of rel.peers) {
          if (!this.idToNodeMap[peer]) {
            await this.ensureNodeLoaded(peer, false, false, false);
          }
        }
      }
    }
  }

  private prepareTreeDiagram() {
    const dia = this.gojsDiagram;

    dia.layout = $$(go.TreeLayout, { alignment: go.TreeLayout.AlignmentStart });

    dia.contextMenu = null;
    dia.mouseOver = null;
    dia.toolManager.draggingTool.isEnabled = false;
    dia.toolManager.dragSelectingTool.isEnabled = false;
  }

  private populatePhysicalTreeDiagram() {
    this.prepareTreeDiagram();

    const dia = this.gojsDiagram;

    const nodeTemplate =
      $$(go.Node, "Spot",
        {
          selectionAdorned: false,
        },
        $$("Button", {
            "ButtonBorder.fill": null,
            "ButtonBorder.stroke": null,
            "_buttonFillOver": null,
            "_buttonStrokeOver": null,
            '_buttonFillPressed': null,
            '_buttonStrokePressed': null,
            click: (e,obj) => {
              const data = obj?.part?.data;
              const id = data.id;
              if (this.idToNodeMap[id]) {
                this.router.navigateByUrl(`/ViewCMDB/${id}`);
              }
            },
          },
          $$(go.Panel, "Auto",
            $$(go.Shape, "RoundedRectangle", new go.Binding("fill", "fill")),
            $$(go.TextBlock,
              new go.Binding("text", "label"))
          ),
        ),
        $$("TreeExpanderButton",
          { alignment: go.Spot.Right, alignmentFocus: go.Spot.Left },
          { visible: true }),
      );

    const linkTemplate =
      $$(go.Link,
        { routing: go.Link.Orthogonal, corner: 5 },
        $$(go.Shape));

    const nodeTemplateMap = new go.Map<string, go.Part>([{
      key: "", value: nodeTemplate
    }]);

    const linkTemplateMap = new go.Map<string, go.Link>([{
      key: "", value: linkTemplate
    }]);

    dia.nodeTemplateMap = nodeTemplateMap;
    dia.linkTemplateMap = linkTemplateMap;

    let noMetadataKey = 1;

    const nodeDataArray: any[] = [];

    const rootMetadata = this.metadata.find((m) => {
      return m.Table.value === "CMDB";
    });

    const root = {
      key: rootMetadata?.id || ++noMetadataKey,
      id: null,
      label: "CMDB",
      fill: "#C6D7EC",
    };

    nodeDataArray.push(root);

    Object.getOwnPropertyNames(this.tableHierarchy).forEach((table) => {
      const parentTable = this.tableHierarchy[table];

      const metadata = this.metadata.find((m) => {
        return m.Table.value === table;
      });

      const parentMetadata = this.metadata.find((m) => {
        return m.Table.value === parentTable;
      });

      const node = {
        key: metadata?.id || ++noMetadataKey,
        id: metadata?.id,
        label: metadata?.Table?.displayValue || `No metadata [${table}]`,
        fill: metadata ? "#C6D7EC" : "red",
        parent: parentMetadata?.id
      };

      nodeDataArray.push(node);
    });

    this.nodes.forEach((c) => {
      const metadata = this.metadata.find((m) => {
        return m.Table.value === c.TableClass.value;
      });

      const node = {
        key: c.id,
        id: c.id,
        label: c.Name.displayValue,
        fill: "#F4D2B8",
        parent: metadata.id
      };

      nodeDataArray.push(node);

    });

    dia.model = new go.TreeModel(nodeDataArray);

    this.cdr.detectChanges();
  }

  private populateLogicalTreeDiagram() {
    this.prepareTreeDiagram();

    const dia = this.gojsDiagram;

    const nodeTemplate =
      $$(go.Node, "Spot",
        { selectionAdorned: false },
        $$(go.Panel, "Auto",
          $$(go.Shape, "RoundedRectangle", new go.Binding("fill", "fill")),
          $$(go.TextBlock,
            new go.Binding("text", "label"))
        ),
        $$("TreeExpanderButton",
          { alignment: go.Spot.Right, alignmentFocus: go.Spot.Left },
          { visible: true }),
      );

    const linkTemplate =
      $$(go.Link,
        { routing: go.Link.Orthogonal, corner: 5 },
        $$(go.Shape));

    const nodeTemplateMap = new go.Map<string, go.Part>([{
      key: "", value: nodeTemplate
    }]);

    const linkTemplateMap = new go.Map<string, go.Link>([{
      key: "", value: linkTemplate
    }]);

    dia.nodeTemplateMap = nodeTemplateMap;
    dia.linkTemplateMap = linkTemplateMap;

    const strokes = {
      "tier": "#699E42",
      "group": "#9FD07C",
      "domain": "#F1F8EC"
    };

    const nodeDataArray: any[] = [];

    const root = {
      key: "0",
      label: "Base",
      fill: "white",
    };

    nodeDataArray.push(root);

    this.logicalClassifications.forEach((c) => {
      const type = c.Type.value as string;
      const node = {
        key: c.id,
        id: c.id,
        label: c.Name.displayValue,
        fill: strokes[type],
        parent: c.Parent?.value || root.key
      };

      nodeDataArray.push(node);
    });

    this.metadata.forEach((c) => {
      const node = {
        key: c.id,
        id: c.id,
        label: c.Name.displayValue,
        fill: "white",
        parent: c?.LogicalDomain?.value
      };

      nodeDataArray.push(node);
    });

    dia.model = new go.TreeModel(nodeDataArray);

    this.cdr.detectChanges();
  }

  private getImageForNode(node: CmdbNodeRecordModel) {
    return this.getNodeMetadataProperty(node, 'Image');
  }

  private getIconForNode(node: CmdbNodeRecordModel) {
    const code = this.getNodeMetadataProperty(node, 'Icon');
    return code ? code : null;
  }

  private getIconNameForNode(node: CmdbNodeRecordModel) {
    return this.getNodeMetadataProperty(node, 'Icon');
  }

  private getNodeMetadataProperty(node: CmdbNodeRecordModel, property: string) {
    const metadata: CmdbMetadataRecordModel[] = this.getMetadataForNode(node);
    return (metadata && metadata.length > 0 && metadata[0][property] && metadata[0][property].value) ? metadata[0][property].value : null;
  }

  private getMetadataForNode(node: CmdbNodeRecordModel) {
    return this.metadata.filter (m => m.Table.value === node.TableClass.value);
  }

  private generateMultipleNodeData(nodes: CmdbNodeRecordModel[]): CmdbNodeDataModel[] {
    return nodes.map((node) => {
      return this.generateNodeData(node);
    }).filter((x) => x !== null);
  };

  private generateMultipleLinkData(links: CmdbRelationshipRecordModel[]): CmdbRelationshipDataModel[] {
    return this.flatten(links.map((rel) => {
      return this.generateLinkData(rel);
    }).filter((x) => x !== null));
  };

  private generateMultiplePeerLinkData(links: CmdbPeerRelationshipRecordModel[]): CmdbPeerRelationshipDataModel[] {
    return this.flatten(links.map((rel) => {
      return this.generatePeerLinkData(rel);
    }).filter((x) => x !== null));
  };

  private generateNodeData(node: CmdbNodeRecordModel): CmdbNodeDataModel {
    const table = node.TableClass.value;
    const id = node.id;
    const label = node.Name.displayValue;
    const image = this.getImageForNode(node);
    const icon = this.getIconForNode(node);
    const iconName = this.getIconNameForNode(node);

    const iconFont = '30pt Material Symbols Outlined';

    return <CmdbNodeDataModel>{
      key: id,
      category: table, // Used to look up templates
      label: label,
      img: image,
      icon: icon,
      iconName: iconName,
      iconFont: iconFont,
      table: table,
      record: node,
      id: id
    };
  };

  private generateLinkData(rel: CmdbRelationshipRecordModel): CmdbRelationshipDataModel {
    const table = rel._tableClass;
    const id = rel.id;
    const fromId = rel?.Provider?.value;
    const toId = rel?.Dependent?.value;
    const linkTypeId = rel.Type?.value as string;
    const label = linkTypeId ? this.idToRelationshipTypesMap[linkTypeId]?.ProviderLabel?.displayValue : "";

    if (!fromId || !toId) {
     return null;
    } else {
      return {
        key: id,
        category: table, // Used to look up templates
        label: label,
        from: fromId as string,
        to: toId as string,
        table: table,
        record: rel,
        id: id
      }
    }
  }

  private generatePeerLinkData(rel: CmdbPeerRelationshipRecordModel): CmdbPeerRelationshipDataModel[] {
    const table = rel._tableClass;
    const id = rel.id;
    const linkTypeId = rel.Type?.value as string;
    const label = linkTypeId ? this.idToPeerRelationshipTypesMap[linkTypeId]?.PeerLabel?.displayValue : "";
    const peers = rel.peers || [];

    const pairs = [];
    for (let i = 0; i < peers.length; i++) {
      for (let j = i + 1; j < peers.length; j++) {
        pairs.push([peers[i], peers[j]]);
      }
    }

    return pairs.map((p, idx) => {
      return {
        key: id + '-' + idx,
        category: table,
        label: label,
        from: p[0],
        to: p[1],
        table: table,
        record: rel,
        id: id
      }
    });
  }

  private flatten(arr: any[]) {
    return arr.reduce((acc, l) => acc.concat(l), []);
  }

  private removeAllNodes() {
    const model = (this.gojsDiagram.model as GraphLinksModel);
    if (model.nodeDataArray) {
      model.nodeDataArray
        .map((n) => n.key)
        .forEach((k) => {
          model.removeNodeData(model.findNodeDataForKey(k));
        });
    }
  }

  private removeAllLinks() {
    const model = (this.gojsDiagram.model as GraphLinksModel);
    if (model.linkDataArray) {
      model.linkDataArray
        .map((n) => n.key)
        .forEach((k) => {
          model.removeLinkData(model.findLinkDataForKey(k));
        });
    }
  }

  private addNodesToDiagram(nodes: CmdbNodeRecordModel[]) {
    const model = (this.gojsDiagram.model as GraphLinksModel);
    const newNodes = this.generateMultipleNodeData(nodes);

    newNodes.forEach((n) => {
      if (!model.findNodeDataForKey(n.key)) {
        model.addNodeData(n);
      }
    });
  }

  private addLinksToDiagram(links: CmdbRelationshipRecordModel[]) {
    const model = (this.gojsDiagram.model as GraphLinksModel);
    const newLinks = this.generateMultipleLinkData(links);

    newLinks.forEach((n) => {
      if (!model.findLinkDataForKey(n.key)) {
        model.addLinkData(n);
      }
    });
  }

  private addPeerLinksToDiagram(links: CmdbPeerRelationshipRecordModel[]) {
    const model = (this.gojsDiagram.model as GraphLinksModel);
    const newLinks = this.generateMultiplePeerLinkData(links);

    newLinks.forEach((n) => {
      if (!model.findLinkDataForKey(n.key)) {
        model.addLinkData(n);
      }
    });
  }

  private zoomIn() {
    this.gojsDiagram.commandHandler.increaseZoom();
  }

  private zoomOut() {
    this.gojsDiagram.commandHandler.decreaseZoom();
  }

  private resetZoom() {
    this.gojsDiagram.commandHandler.resetZoom();
  }

  private zoomToFit() {
    this.gojsDiagram.commandHandler.zoomToFit();
    // The line below will fit the entire document which means that a single node will be enormous.
    // this.gojsDiagram.zoomToRect(this.gojsDiagram.documentBounds);
  }

  private resetDiagram() {
    this.removeAllNodes();
    this.removeAllLinks();
    this.displayDiagram();
  }

  // Context menu individual operations

  private inspectNode(data: CmdbNodeDataModel) {
    const id = data.id;
    const table = data.table;
    this.legacyNavigateService.rightSlideInWindow({
      'tile': 'DetailsAutoScaffold',
      'parameter-values': {
        table: table,
        id: id
      },
      'loadOptions' : {
        cache : false
      }
    });
  }

  private focusCI(data: CmdbNodeDataModel) {
    const model = (this.gojsDiagram.model as GraphLinksModel);
    model.nodeDataArray
      .map((n) => n.key)
      .filter((k) => k != data.key)
      .forEach((k) => {
        model.removeNodeData(model.findNodeDataForKey(k));
      });

    model.linkDataArray
      .map((n) => n.key)
      .forEach((k) => {
      model.removeLinkData(model.findLinkDataForKey(k));
    });

    this.cdr.detectChanges();
  }

  private removeCI(data: CmdbNodeDataModel) {
    const model = (this.gojsDiagram.model as GraphLinksModel);

    const linkIdsToRemove = [];
    model.linkDataArray.forEach((n) => {
      if (n.from === data.key || n.to === data.key) {
        linkIdsToRemove.push(n.key);
      }
    });

    linkIdsToRemove.forEach((k) => {
      model.removeLinkData(model.findLinkDataForKey(k));
    })

    model.removeNodeData(data);

    this.cdr.detectChanges();
  }

  private expandDependents(data: CmdbNodeDataModel | string) {
    const id = typeof data === "string" ? data : data.id;

    this.ensureNodeLoaded(id, true, false, false).then(res => {

      const dependentLinks = this.relationships.reduce((acc, r) => {
        if (r.Provider.value === id) {
          acc.push(r);
        }
        return acc;
      }, []);

      const dependentNodeIds = dependentLinks.map((r) => {
        return r.Dependent.value;
      });

      const dependentNodes = dependentNodeIds.map((r) => {
        return this.idToNodeMap[r];
      });

      this.addNodesToDiagram(dependentNodes);

      this.addLinksToDiagram(dependentLinks);

      this.cdr.detectChanges();
    });
  }

  private alreadyAddedNode(key: string) {
    const model = (this.gojsDiagram.model as GraphLinksModel);
    return model.findNodeDataForKey(key) != null;
  }

  private alreadyAddedLink(key: string) {
    const model = (this.gojsDiagram.model as GraphLinksModel);

    return model.findLinkDataForKey(key) != null;
  }

  private expandAllDependents(data: CmdbNodeDataModel | string) {
    const model = (this.gojsDiagram.model as GraphLinksModel);

    const id = typeof data === "string" ? data : data.id;

    const dependentLinks = this.relationships.reduce((acc, r) => {
      if (r.Provider.value === id && !this.alreadyAddedLink(r.id)) {
        acc.push(r);
      }
      return acc;
    }, []);

    const dependentNodeIds = dependentLinks.map((r) => {
      return r.Dependent.value;
    });

    const dependentNodes = dependentNodeIds.map((r) => {
      return this.idToNodeMap[r];
    });

    this.addNodesToDiagram(dependentNodes);

    this.addLinksToDiagram(dependentLinks);

    const newNodeDatas = model.nodeDataArray.filter((n) => {
      return dependentNodeIds.includes(n.key);
    });

    newNodeDatas.forEach((n) => {
      this.expandAllDependents(n as CmdbNodeDataModel);
    });

    this.cdr.detectChanges();
  }

  private expandProviders(data: CmdbNodeDataModel | string) {
    const model = (this.gojsDiagram.model as GraphLinksModel);

    const id = typeof data === "string" ? data : data.id;

    this.ensureNodeLoaded(id, false, true, false).then(res => {

      const providerLinks = this.relationships.reduce((acc, r) => {
        if (r.Dependent.value === id) {
          acc.push(r);
        }
        return acc;
      }, []);

      const providerNodeIds = providerLinks.map((r) => {
        return r.Provider.value;
      });

      const providerNodes = providerNodeIds.map((r) => {
        return this.idToNodeMap[r];
      });

      this.addNodesToDiagram(providerNodes);

      this.addLinksToDiagram(providerLinks);

      this.cdr.detectChanges();
    });
  }

  private expandAllProviders(data: CmdbNodeDataModel | string) {
    const model = (this.gojsDiagram.model as GraphLinksModel);

    const id = typeof data === "string" ? data : data.id;

    const providerLinks = this.relationships.reduce((acc, r) => {
      if (r.Dependent.value === id && !this.alreadyAddedLink(r.id)) {
        acc.push(r);
      }
      return acc;
    }, []);

    const providerNodeIds = providerLinks.map((r) => {
      return r.Provider.value;
    });

    const providerNodes =  providerNodeIds.map((r) => {
      return this.idToNodeMap[r];
    });

    this.addNodesToDiagram(providerNodes);

    this.addLinksToDiagram(providerLinks);

    const newNodeDatas = model.nodeDataArray.filter((n) => {
      return providerNodeIds.includes(n.key);
    });

    newNodeDatas.forEach((n) => {
      this.expandAllProviders(n as CmdbNodeDataModel);
    });

    this.cdr.detectChanges();
  }

  private expandPeers(data: CmdbNodeDataModel | string) {
    const id = typeof data === "string" ? data : data.id;

    this.ensureNodeLoaded(id, false, false, true).then(res => {

      const peerLinks = this.peerRelationships.filter((p) => p.peers.includes(id));

      const peerNodes = this.flatten(peerLinks.map((r) => {
        return r.peers.map((p) => {
          return this.idToNodeMap[p];
        })
      }));

      this.addNodesToDiagram(peerNodes);

      this.addPeerLinksToDiagram(peerLinks);

      this.cdr.detectChanges();
    });
  }

  private expandAll(data: CmdbNodeDataModel | string) {
    this.expandProviders(data);
    this.expandDependents(data);
    this.expandPeers(data);
  }

  // Info box methods

  private initToolTip() {
    return $$(go.HTMLInfo, {
      show: (obj: go.GraphObject, diagram: go.Diagram, tool: go.Tool) => { this.showToolTip(obj, diagram, tool) },
      hide: (diagram: go.Diagram, tool: go.Tool) => { this.hideToolTip(diagram, tool) }
    });
  }

  private showToolTip(obj: go.GraphObject, diagram: go.Diagram, tool: go.Tool) {
    const data = obj?.part?.data;
    const e = diagram.lastInput;
    this.updateInfoBox(e.viewPoint, data);
  }

  private hideToolTip(diagram: go.Diagram, tool: go.Tool) {
    const elem = document.getElementById("cmdb-info-box-holder");
    if (elem) {
      elem.innerHTML = "";
    }
    this.lastInfoboxId = null;
  }

  private updateInfoBox(mousePos: go.Point, data: CmdbNodeDataModel) {
    const box = document.getElementById("cmdb-info-box-holder");

    // Check to see if the infobox exists and is for the current node. If not we build a new one.
    if (!(this.lastInfoboxId && this.lastInfoboxId === data.id)) {

      box.innerHTML = "";

      const infobox = document.createElement("div");
      infobox.id = "cmdb-info-box";
      box.appendChild(infobox);

      const metadata: CmdbMetadataRecordModel[] = this.metadata.filter(m => m.Table.value === data.table);
      if (metadata && metadata.length > 0 && metadata[0].InfoboxTemplate.value) {
        const implementation = metadata[0].InfoboxTemplate.value;
        const comp: any = document.createElement("sb-ng-cmdb-infobox");
        comp.implementation = implementation;
        comp.data = data;
        infobox.appendChild(comp);
      } else {
        const comp: any = document.createElement("sb-ng-cmdb-default-infobox");
        comp.data = data;
        infobox.appendChild(comp);
      }

      this.lastInfoboxId = data.id;
    }

    box.style.left = mousePos.x + 30 + "px";
    box.style.top = mousePos.y + 20 + "px";
  }

  // Fake button navigation

  buttonLabel(key: string): string {
    return this.i18nService.getMessage(key);
  }

  canGoBack() {
    return this.legacyNavigateService.canGoBack();
  }

  back() {
    this.legacyNavigateService.back();
  }


}
